'use client';

import { HStack, VStack } from '@terra/ui-styled-system';
import {
  CheckmarkCircle01Icon,
  ArrowRight01Icon,
  ExpandIcon,
  Cancel01Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { Button } from '../../../components/styled/button';
import { Text } from '../../../components/styled/text';
import type {
  PromptOption,
  NodeConfirmationMessage,
} from '@terra/shared-dto-chat-types';
import { MessageSkeletonCard } from './MessageSkeletonCard';
import { Collapsible } from '../../../components/collapsible';
import { ArrowDown01Icon } from '@terra/ui-icons';
import { useState } from 'react';
import { MarkdownPreview } from '../../markdown-preview';
import { LegacyDialog } from '../../../components/dialog';
import { IconButton } from '../../../components/icon-button';
import { dump } from 'js-yaml';

const getNodePayload = (args?: Record<string, unknown>) => {
  if (!args) {
    return null;
  }

  return `\`\`\`yaml\n${dump(args)}\n\`\`\``;
};

type NodeArgsViewerData = {
  nodeName: string;
  nodePayload: string;
};

export type OnOpenNodeArgsInMainContent = (data: NodeArgsViewerData) => void;
export function NodeConfirmationPromptCard({
  dataMessage,
  selectedValue,
  onDataMessageEvent,
  onOpenNodeArgsInMainContent,
}: {
  selectedValue?: string;
  dataMessage?: NodeConfirmationMessage;
  onDataMessageEvent?: (event: {
    dataMessage: NodeConfirmationMessage;
    selectedOption: PromptOption;
  }) => void;
  onOpenNodeArgsInMainContent?: OnOpenNodeArgsInMainContent;
}) {
  const t = useTranslations('designSystem.chat.nodeConfirmationPromptCard');
  const tCommon = useTranslations('designSystem.common');
  const nodeConfirmationPrompt = dataMessage?.nodeConfirmationPrompt;
  const nodeName = nodeConfirmationPrompt?.nodeName;
  const nodeArgs = nodeConfirmationPrompt?.args;
  const [isOpen, setIsOpen] = useState(
    selectedValue?.toLowerCase() === 'approve' ? false : Boolean(nodeArgs),
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  if (!nodeConfirmationPrompt) {
    return null;
  }

  const selectedOption = selectedValue
    ? nodeConfirmationPrompt.options.find(
        (option) => option.value === selectedValue,
      )
    : undefined;
  const nodePayload = getNodePayload(nodeArgs);

  return (
    <MessageSkeletonCard
      type="toolInvocation"
      header={
        <HStack gap={2} justifyContent="space-between" width="100%">
          <HStack gap={2}>
            {selectedValue ? (
              <>
                <CheckmarkCircle01Icon size="24px" />
                <Text fontSize="sm" fontWeight="bolder">
                  {nodeName}
                </Text>
              </>
            ) : (
              <Text fontSize="sm" fontWeight="bolder">
                {nodeName}
              </Text>
            )}
            <Text fontSize="sm" fontWeight="bolder">
              {`| Execution Cycle #${nodeConfirmationPrompt.cycle}`}
            </Text>
            {selectedValue && (
              <Text fontSize="sm" fontWeight="bolder">
                -{' '}
                {selectedValue.toLowerCase() === 'approve'
                  ? t('approved')
                  : t('rejected')}
              </Text>
            )}
          </HStack>
          {!!nodePayload && (
            <IconButton
              variant="ghost"
              size="md"
              aria-label={tCommon('expand')}
              onClick={() => {
                if (onOpenNodeArgsInMainContent && nodeName) {
                  onOpenNodeArgsInMainContent({
                    nodeName,
                    nodePayload,
                  });
                } else {
                  setIsDialogOpen(true);
                }
              }}
            >
              <ExpandIcon />
            </IconButton>
          )}
        </HStack>
      }
      footer={
        <HStack gap={2} flexDirection="row-reverse">
          {selectedValue ? (
            <Button size="sm" variant="outline" disabled>
              {selectedOption?.label || selectedValue}
            </Button>
          ) : (
            nodeConfirmationPrompt.options.map((option, index) => (
              <Button
                size="sm"
                key={index}
                variant="outline"
                onClick={() =>
                  onDataMessageEvent?.({ dataMessage, selectedOption: option })
                }
              >
                {option.label}
              </Button>
            ))
          )}
        </HStack>
      }
    >
      <VStack alignItems="start" gap={3}>
        <MarkdownPreview source={nodeConfirmationPrompt.description} />
        {nodePayload && (
          <Collapsible.Root
            lazyMount
            unmountOnExit
            open={isOpen}
            onOpenChange={({ open }) => setIsOpen(open)}
          >
            <Collapsible.Trigger asChild>
              <HStack
                justifyContent="space-between"
                alignItems="center"
                cursor="pointer"
              >
                {isOpen && !!nodeArgs ? (
                  <ArrowDown01Icon size={24} />
                ) : (
                  <ArrowRight01Icon size={24} />
                )}
                <Text fontSize="sm" fontWeight="bolder">
                  {isOpen ? 'Hide' : 'Show'}
                </Text>
              </HStack>
            </Collapsible.Trigger>
            <Collapsible.Content minWidth="200px">
              <MarkdownPreview source={nodePayload} />
            </Collapsible.Content>
          </Collapsible.Root>
        )}
      </VStack>
      {nodePayload && (
        <LegacyDialog.Root
          open={isDialogOpen}
          onOpenChange={({ open }) => setIsDialogOpen(open)}
        >
          <LegacyDialog.Content
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              maxWidth: '100vw',
              maxHeight: '100vh',
              margin: 0,
              padding: 0,
              zIndex: 9999,
            }}
          >
            <VStack gap={4} height="100%">
              <HStack
                justifyContent="space-between"
                alignItems="center"
                width="100%"
                p={4}
              >
                <LegacyDialog.Title>
                  {t('nodeArguments', { nodeName: nodeName ?? '' })}
                </LegacyDialog.Title>
                <LegacyDialog.CloseTrigger asChild>
                  <IconButton aria-label={tCommon('close')} variant="ghost">
                    <Cancel01Icon />
                  </IconButton>
                </LegacyDialog.CloseTrigger>
              </HStack>
              <VStack flex={1} width="100%" overflow="auto" p={4}>
                <MarkdownPreview source={nodePayload} />
              </VStack>
            </VStack>
          </LegacyDialog.Content>
        </LegacyDialog.Root>
      )}
    </MessageSkeletonCard>
  );
}
